import {
  InstallmentSubscription,
  PausedSubscriptionStatus,
  PaymentResponseModel,
  StripeSubscriptionResponse,
  StripeSubscriptionStatus
} from '@flocasts/payments'
import { addMonths, addYears, isValid, parseISO } from 'date-fns'
import { MinimalSubscription, MinimalPlan } from './subscriptions.interface'
import { Offer, PeriodEnum, PlatformEnum } from '@flocasts/offers'
import { SupplementedUserSubscription } from 'src/app/account-settings/account-settings.interfaces'

export const isStripeSubscriptionResponse = (payment: PaymentResponseModel): payment is StripeSubscriptionResponse => {
  return payment.offers[0].type === PlatformEnum.Stripe && 'stripeId' in payment
}

/**
 * if user is active and is on a monthly plan or is on a vertical specific plan
 */
export function canUpgradeSubscription(isActive: boolean, offer: Offer): boolean {
  if (!isActive || offer.installment) return false

  return offer.period !== PeriodEnum.Year
}

/**
 * If user subscription is active or in trial, they can cancel
 */
export function canCancelSubscription(status: string): boolean {
  return status === StripeSubscriptionStatus.Active || status === StripeSubscriptionStatus.Trial
}

export function getInstallmentSubscription(payment: PaymentResponseModel): InstallmentSubscription | undefined {
  return isStripeSubscriptionResponse(payment) && payment.installmentSubscription
    ? payment.installmentSubscription
    : undefined
}

/**
 * Render new Payment into MinimalSubscription object for backwards compatibility with SupplementedUserSubscription
 */
export const renderMinimalUserSubscription = (
  payment: StripeSubscriptionResponse | PaymentResponseModel,
  stripeKey: string,
  now = new Date()
): MinimalSubscription | undefined => {
  const endDate = parseISO(payment.accessEndDate)
  if (!isValid(endDate)) return undefined

  const offer = payment.offers[0]
  const isActive = payment.status === 'active'

  const minimalPlan: MinimalPlan = {
    type: 'plan',
    id: offer.id,
    product_id: isStripeSubscriptionResponse(payment) ? payment.stripeId : null,
    name: offer.name,
    monthly: offer.period === 'month',
    yearly: offer.period === 'year',
    period: offer.period === 'month' ? 'month' : 'year', // for backwards compatibility this has to be coerced to 'month' or 'year'
    cost_per_month: {
      localized_text: offer.price.monthly.localizedText,
      currency: offer.price.monthly.currency,
      amount: offer.price.monthly.amount,
      scale: offer.price.monthly.scale
    },
    payment_provider: {
      public_key: stripeKey
    }
  }

  const installmentSubscription: InstallmentSubscription | undefined = getInstallmentSubscription(payment)
  const isStripeSubscription = isStripeSubscriptionResponse(payment)

  const subscriptionIsResumed = !!(
    isStripeSubscription &&
    payment.pausedSubscription &&
    payment.pausedSubscription.pauseStatus === PausedSubscriptionStatus.RESUMED
  )
  const subscriptionisPaused = !!(
    isStripeSubscription &&
    payment.pausedSubscription &&
    payment.pausedSubscription.pauseStatus === PausedSubscriptionStatus.PAUSED
  )
  const subscriptionisPendingPause = !!(
    isStripeSubscription &&
    payment.pausedSubscription &&
    payment.pausedSubscription.pauseStatus === PausedSubscriptionStatus.PENDING
  )

  return {
    id: payment.id,
    status: payment.status,
    customer_id: isStripeSubscriptionResponse(payment) ? payment.customerId : undefined,
    end_date: payment.accessEndDate,
    created_at: payment.createdAt,
    is_active: isActive,
    is_expired: endDate < now,
    is_stripe: offer.type === PlatformEnum.Stripe,
    plan: minimalPlan,
    paused_subscription: isStripeSubscription ? payment.pausedSubscription : null,
    pause_dates: isStripeSubscription ? payment.pauseDates : [],
    is_paused: subscriptionisPaused,
    is_pending_pause: subscriptionisPendingPause,
    can_pause: !!((isStripeSubscription && !payment.pausedSubscription) || subscriptionIsResumed),
    can_resume: subscriptionisPaused || subscriptionisPendingPause,
    offers: payment.offers,
    site_id: offer.siteId,
    card_last4: isStripeSubscriptionResponse(payment) && payment.cardLast4 ? payment.cardLast4 : undefined,
    purchase_date: payment.createdAt,
    card_type: isStripeSubscriptionResponse(payment) && payment.cardType ? payment.cardType : undefined,
    plan_is_yearly: offer.period === 'year',
    can_upgrade: isStripeSubscriptionResponse(payment) && canUpgradeSubscription(isActive, offer),
    can_cancel: canCancelSubscription(payment.status),
    discount: isStripeSubscriptionResponse(payment) && payment.discount ? payment.discount : undefined,
    is_canceled: payment.status === StripeSubscriptionStatus.Cancelled,
    upcoming_payment_date:
      isStripeSubscriptionResponse(payment) && payment.upcomingPaymentDate ? payment.upcomingPaymentDate : undefined,
    installment_subscription: installmentSubscription
  }
}

export const calculateSubscriptionEndDate = (subscription: SupplementedUserSubscription | MinimalSubscription) => {
  if (subscription.end_date) return subscription.end_date

  const purchaseDate = new Date(subscription.purchase_date)
  const period = subscription.plan.period
  return period === 'month' ? addMonths(purchaseDate, 1).toDateString() : addYears(purchaseDate, 1).toDateString()
}

// SupplementedUserSubscription is only used in the old payments architecture
// MinimalSubscription is used in the new architecture
export const isSupplementedUserSubscription = (
  subscription: SupplementedUserSubscription | MinimalSubscription | undefined
): subscription is SupplementedUserSubscription => {
  return subscription !== undefined && 'is_roku' in subscription && 'is_itunes' in subscription
}
